import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import { Users, Clock, Activity, Award, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import TrialBanner from "@/components/TrialBanner";

export default function DashboardPage() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/dashboard/summary");
                setData(data);
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    return (
        <div data-testid="dashboard-page">
            <div className="mb-8">
                <div className="label-eyebrow mb-2">Dashboard</div>
                <h1 className="font-display text-4xl md:text-5xl text-navy">
                    Buongiorno,{" "}
                    <span className="italic text-amber-brand">
                        {user?.name?.split(" ")[0] || "Manager"}
                    </span>
                </h1>
                <p className="text-sf-secondary mt-2">
                    Ecco cosa sta succedendo nella tua sala questa settimana.
                </p>
            </div>

            <TrialBanner user={user} />

            <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mb-10">
                <Kpi
                    icon={Users}
                    label="Dipendenti attivi"
                    value={loading ? "—" : data?.total_employees ?? 0}
                    testid="kpi-employees"
                />
                <Kpi
                    icon={Clock}
                    label="Ore pianificate / sett."
                    value={loading ? "—" : `${data?.hours_scheduled_week ?? 0}h`}
                    testid="kpi-hours"
                />
                <Kpi
                    icon={Activity}
                    label="Tasso puntualità"
                    value={loading ? "—" : `${data?.attendance_rate ?? 0}%`}
                    testid="kpi-attendance"
                />
                <Kpi
                    icon={Award}
                    label="Turni settimana"
                    value={loading ? "—" : data?.shifts_count_week ?? 0}
                    testid="kpi-shifts"
                />
            </div>

            <div className="grid md:grid-cols-3 gap-5">
                <div
                    className="md:col-span-2 bg-white border border-sf rounded-xl p-7"
                    data-testid="top-performer-card"
                >
                    <div className="label-eyebrow mb-2">Top performer</div>
                    {data?.top_performer ? (
                        <>
                            <div className="font-display text-4xl text-navy mb-4">
                                {data.top_performer.name}
                            </div>
                            <div className="grid grid-cols-3 gap-6 mt-2">
                                <Stat
                                    label="Coperti"
                                    value={data.top_performer.coperti}
                                />
                                <Stat
                                    label="Mance"
                                    value={`€${data.top_performer.tips}`}
                                />
                                <Stat
                                    label="Rating"
                                    value={data.top_performer.avg_rating}
                                />
                            </div>
                        </>
                    ) : (
                        <div className="text-sf-muted py-6">
                            Nessun dato performance ancora. Aggiungi i record
                            nella sezione Performance.
                        </div>
                    )}
                </div>
                <div
                    className="bg-amber-brand rounded-xl p-7 text-navy"
                    data-testid="quick-actions-card"
                >
                    <div className="label-eyebrow mb-2">Azioni rapide</div>
                    <div className="font-display text-2xl mb-5">
                        Cosa vuoi fare?
                    </div>
                    <div className="space-y-2">
                        <Link to="/app/dipendenti" className="block">
                            <Button
                                variant="outline"
                                className="w-full bg-white border-navy text-navy lift"
                                data-testid="qa-add-employee"
                            >
                                + Nuovo dipendente
                            </Button>
                        </Link>
                        <Link to="/app/turni" className="block">
                            <Button
                                variant="outline"
                                className="w-full bg-white border-navy text-navy lift"
                                data-testid="qa-add-shift"
                            >
                                + Nuovo turno
                            </Button>
                        </Link>
                        <Link to="/app/performance" className="block">
                            <Button
                                variant="outline"
                                className="w-full bg-white border-navy text-navy lift"
                                data-testid="qa-add-performance"
                            >
                                + Registra performance
                            </Button>
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Kpi({ icon: Icon, label, value, testid }) {
    return (
        <div
            className="bg-white border border-sf rounded-xl p-6 lift hover:shadow-md"
            data-testid={testid}
        >
            <div className="flex items-center justify-between mb-4">
                <div className="label-eyebrow">{label}</div>
                <Icon size={18} className="text-amber-brand" />
            </div>
            <div className="font-display text-4xl text-navy">{value}</div>
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div>
            <div className="label-eyebrow mb-1">{label}</div>
            <div className="font-display text-2xl text-navy">{value}</div>
        </div>
    );
}
